import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { CardEvidencePanel, CardEvidenceRow, MetricTone } from 'containers/Intelligence/types';

import { formatSharePercent, sharePercent } from '../formatMetric';
import { panelHeading } from '../panelHeading';
import { STATUS_PILL_HUES, statusPill, statusPillLabel } from '../statusPill';

export interface IEvidencePanelProps extends CardEvidencePanel {
  className?: string;
  /**
   * Draws each row's value as the shared tinted pill instead of plain text, taking the hue from that
   * row's own status.tone; a row without a tone stays plain, which is what the mixed Financial
   * Summary table needs (3583-8825 pills only Income Stability, never the amounts beside it).
   * Off by default because most frames draw this panel as plain label/value pairs.
   *
   * The tone is payload-driven rather than read off the level word because the level word alone
   * cannot choose the hue: High is green on Income Stability (A-sec-3583-8776) and red on Churn Risk
   * (A-z5-3583-9477). PLA-8828.
   */
  valueAsPill?: boolean;
}

const SHARE_TRACK_WIDTH = 96;

// statusPill speaks its own tone vocabulary, in which accent has no hue and takes the neutral one —
// the reading RiskEvidenceSection.tsx:224-231 already makes of the same record. PLA-8828.
const TONE_PILL_HUES: Record<MetricTone, string> = {
  positive: STATUS_PILL_HUES.positive,
  warning: STATUS_PILL_HUES.warning,
  negative: STATUS_PILL_HUES.negative,
  info: STATUS_PILL_HUES.info,
  accent: STATUS_PILL_HUES.neutral
};

const PanelWrapper = styled.section`
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
`;

const Label = styled.h4`
  ${panelHeading}
`;

const RowList = styled.ul`
  margin: ${tokens.space['8']} 0 0;
  padding: 0;
  list-style: none;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const RowLabel = styled.span`
  flex: 0 1 auto;
  margin-right: ${tokens.space['12']};
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

const RowValue = styled.span`
  flex: 1 1 auto;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  text-align: right;
`;

const ValuePill = styled.span<{ $hue: string }>`
  ${statusPill}
`;

const ValuePillLabel = styled.span`
  ${statusPillLabel}
`;

const ShareTrack = styled.span`
  display: block;
  width: ${SHARE_TRACK_WIDTH}px;
  max-width: 100%;
  height: 6px;
  margin-top: ${tokens.space['4']};
  border-radius: ${tokens.radius.pill};
  background: ${tokens.color['track-bg']};
  overflow: hidden;
`;

const ShareFill = styled.span`
  display: block;
  height: 100%;
  border-radius: inherit;
  background: ${tokens.color['accent-blue']};
`;

const Note = styled.p`
  margin: ${tokens.space['12']} 0 0;
  padding: ${tokens.space['12']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-card']};
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const hasShare = (row: CardEvidenceRow): boolean => Number.isFinite(row.share);

const EvidencePanel: React.FC<IEvidencePanelProps> = ({
  label,
  rows,
  note,
  valueAsPill = false,
  className
}) => {
  const evidenceRows = Array.isArray(rows) ? rows : [];

  // The FR forbids fabricating evidence, so a panel with nothing measured leaves no titled shell
  // behind — the card shows its Insufficient Data state instead.
  if (evidenceRows.length === 0 && !note) {
    return null;
  }

  return (
    <PanelWrapper className={className} data-testid="evidence-panel">
      <Label data-testid="evidence-panel-label">{label}</Label>
      {evidenceRows.length > 0 ? (
        <RowList>
          {evidenceRows.map(row => {
            // An absent share means the share was never measured, which a zero-width bar would
            // misreport as a measured 0 — so only a real number draws a track.
            const width = hasShare(row) ? sharePercent(row.share) : null;
            const pillTone = valueAsPill ? row.status?.tone : undefined;

            return (
              <li key={row.label} data-testid="evidence-panel-row">
                <RowHeader>
                  <RowLabel data-testid="evidence-panel-row-label">{row.label}</RowLabel>
                  <RowValue data-testid="evidence-panel-row-value">
                    {pillTone ? (
                      <ValuePill
                        $hue={TONE_PILL_HUES[pillTone]}
                        data-testid="evidence-panel-row-value-pill"
                      >
                        <ValuePillLabel>{row.value}</ValuePillLabel>
                      </ValuePill>
                    ) : (
                      row.value
                    )}
                  </RowValue>
                </RowHeader>
                {width === null ? null : (
                  <ShareTrack
                    role="img"
                    aria-label={formatSharePercent(row.share)}
                    data-testid="evidence-panel-row-share"
                  >
                    <ShareFill style={{ width: `${width}%` }} />
                  </ShareTrack>
                )}
              </li>
            );
          })}
        </RowList>
      ) : null}
      {note ? <Note data-testid="evidence-panel-note">{note}</Note> : null}
    </PanelWrapper>
  );
};

export default EvidencePanel;
